import { captionsFromWordTimings, toWebVtt } from './captions.js';
import { sha256Hex, synthesisCacheKey } from './hash.js';
import type {
  ConsentReceipt,
  CostEstimate,
  ITextToSpeechService,
  PronunciationDictionarySyncRequest,
  PronunciationDictionarySyncResult,
  SynthesisRequest,
  SynthesisResult,
  TtsCapabilities,
  Voice,
} from './model.js';
import { timingsFromCharacterAlignment, type CharacterAlignment } from './timing.js';

interface ServiceSynthesisResponse {
  readonly audioBase64: string;
  readonly mime: string;
  readonly alignment: CharacterAlignment;
  readonly cached: boolean;
}

/** Browser-safe client for SlideForge's local service. It never receives the ElevenLabs API key. */
export class SlideForgeTtsClient implements ITextToSpeechService {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;

  constructor(options: { readonly baseUrl?: string; readonly fetch?: typeof fetch } = {}) {
    this.#baseUrl = (options.baseUrl ?? '/api').replace(/\/$/u, '');
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async listVoices(locale?: string): Promise<readonly Voice[]> {
    const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.#json<readonly Voice[]>(await this.#fetch(`${this.#baseUrl}/tts/voices${query}`));
  }

  async synthesize(
    request: SynthesisRequest,
    consent: ConsentReceipt,
    signal?: AbortSignal,
  ): Promise<SynthesisResult> {
    if (consent.destination !== 'ElevenLabs') throw new Error('Consent must name ElevenLabs.');
    const digest = await sha256Hex(request.text);
    if (consent.contentSha256 !== digest) throw new Error('Consent does not cover the current narration text.');

    const response = await this.#fetch(`${this.#baseUrl}/tts/synthesize`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ request, consent }),
      signal,
    });
    const payload = await this.#json<ServiceSynthesisResponse>(response);
    const timings = timingsFromCharacterAlignment(request.text, payload.alignment);
    const captions = captionsFromWordTimings(request.text, timings.words);
    const audio = decodeBase64(payload.audioBase64);
    const durationMs = Math.round(
      (payload.alignment.characterEndTimesSeconds[
        payload.alignment.characterEndTimesSeconds.length - 1
      ] ?? 0) * 1_000,
    );
    return {
      audio,
      mime: payload.mime,
      durationMs,
      wordTimings: timings.words,
      sentenceTimings: timings.sentences,
      captions,
      webVtt: toWebVtt(captions),
      synthesisKey: await synthesisCacheKey(request),
      cached: payload.cached,
    };
  }

  async estimateCost(request: SynthesisRequest): Promise<CostEstimate | null> {
    return { characters: [...request.text].length };
  }

  async syncPronunciationDictionary(
    request: PronunciationDictionarySyncRequest,
    consent: ConsentReceipt,
  ): Promise<PronunciationDictionarySyncResult> {
    const contentJson = pronunciationDictionaryConsentContent(request);
    if (consent.destination !== 'ElevenLabs') throw new Error('Consent must name ElevenLabs.');
    if (consent.contentSha256 !== await sha256Hex(contentJson)) {
      throw new Error('Consent does not cover the current pronunciation lexicon.');
    }
    return this.#json<PronunciationDictionarySyncResult>(
      await this.#fetch(`${this.#baseUrl}/tts/pronunciation-dictionaries`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ contentJson, consent }),
      }),
    );
  }

  capabilities(): TtsCapabilities {
    return {
      ssml: false,
      wordTimings: true,
      phonemes: false,
      styles: ['stability', 'similarity', 'style', 'speed'],
      maxChars: 10_000,
    };
  }

  async saveElevenLabsCredential(apiKey: string): Promise<void> {
    const response = await this.#fetch(`${this.#baseUrl}/credentials/elevenlabs`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ apiKey }),
    });
    await this.#ok(response);
  }

  async #json<T>(response: Response): Promise<T> {
    await this.#ok(response);
    return (await response.json()) as T;
  }

  async #ok(response: Response): Promise<void> {
    if (response.ok) return;
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `TTS service request failed with HTTP ${response.status}.`);
  }
}

/** Exact, stable cloud payload covered by the author's lexicon consent. */
export function pronunciationDictionaryConsentContent(
  request: PronunciationDictionarySyncRequest,
): string {
  return JSON.stringify({
    courseId: request.courseId,
    ...(request.dictionaryId ? { dictionaryId: request.dictionaryId } : {}),
    rules: request.rules.map((rule) => ({
      written: rule.written,
      pronunciation: rule.pronunciation,
      alphabet: rule.alphabet,
    })),
  });
}

function decodeBase64(value: string): Uint8Array {
  const binary = globalThis.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
